import { useSyncExternalStore } from 'react';

/**
 * Theme preference with 3 modes: 'light' | 'dark' | 'system'.
 *
 * - 'system' tracks the OS `prefers-color-scheme` and updates live
 * - 'light' / 'dark' override and persist across sessions
 *
 * The Tailwind config uses `darkMode: 'class'`, so we toggle the `dark` class
 * on `<html>` whenever the resolved theme is 'dark'. To avoid a flash of the
 * wrong theme on page load, `index.html` runs an inline script before React
 * mounts that reads this same storage key and applies the class.
 *
 * Pattern mirrors `sidebar-state.ts`: useSyncExternalStore + a custom event
 * so multiple components stay in sync without a Context provider.
 */

export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const STORAGE_KEY = 'theme';
const EVENT_NAME = 'theme-change';
const MEDIA_QUERY = '(prefers-color-scheme: dark)';

function isTheme(value: unknown): value is Theme {
  return value === 'light' || value === 'dark' || value === 'system';
}

function getStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isTheme(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
}

export function resolveTheme(theme: Theme): ResolvedTheme {
  if (theme === 'system') {
    if (typeof window === 'undefined') return 'light';
    return window.matchMedia(MEDIA_QUERY).matches ? 'dark' : 'light';
  }
  return theme;
}

export function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  const resolved = resolveTheme(theme);
  document.documentElement.classList.toggle('dark', resolved === 'dark');
}

function subscribe(callback: () => void): () => void {
  window.addEventListener(EVENT_NAME, callback);
  window.addEventListener('storage', callback);
  // When user is in 'system' mode, OS-level dark mode flips should propagate
  // immediately. matchMedia.change fires when the OS preference changes.
  const mql = window.matchMedia(MEDIA_QUERY);
  mql.addEventListener('change', callback);
  return () => {
    window.removeEventListener(EVENT_NAME, callback);
    window.removeEventListener('storage', callback);
    mql.removeEventListener('change', callback);
  };
}

function getServerSnapshot(): Theme {
  return 'system';
}

export function useTheme(): {
  theme: Theme;
  resolved: ResolvedTheme;
  setTheme: (next: Theme) => void;
} {
  const theme = useSyncExternalStore(subscribe, getStoredTheme, getServerSnapshot);

  const setTheme = (next: Theme): void => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore — storage unavailable
    }
    applyTheme(next);
    window.dispatchEvent(new Event(EVENT_NAME));
  };

  return {
    theme,
    resolved: resolveTheme(theme),
    setTheme,
  };
}
